import { useEffect, useState } from "react";
import { isPopcorn, type GameDto, type PlayerDto, type Side } from "@chess/shared";

/**
 * Turns a side has completed, which is also its index into the popcorn
 * rotation. Mirrors `turnsTaken` on the server; white moves on odd plies and
 * black on even, so each has taken half the completed moves.
 */
function turnsTakenBy(moveCount: number, side: Side): number {
  return side === "white" ? Math.ceil(moveCount / 2) : Math.floor(moveCount / 2);
}

/** mm:ss, for a duration rather than a countdown. */
function formatElapsed(ms: number): string {
  const total = Math.floor(Math.max(0, ms) / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/**
 * Time on the current move, ticking locally.
 *
 * Counts up from the server's measurement plus the time since it arrived, so a
 * browser whose clock disagrees with the server still shows the right duration.
 */
function TurnTimer({ elapsedMs, asOf, running }: { elapsedMs: number; asOf: number; running: boolean }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);

  const shown = running ? elapsedMs + (now - asOf) : elapsedMs;
  return <span className="team-status__elapsed">{formatElapsed(shown)} on this move</span>;
}

type Standing = "locked" | "thinking" | "waiting" | "on-deck";
interface ResolvedMove {
  san: string;
  chosen: boolean;
}

function PlayerRow({
  player,
  standing,
  isYou,
  online,
  showBallotStats,
  previousMove,
}: {
  player: PlayerDto;
  standing: Standing;
  isYou: boolean;
  online: boolean;
  showBallotStats: boolean;
  /** A completed ballot only — current-turn votes must remain secret. */
  previousMove: ResolvedMove | null;
}) {
  // Never colour alone: each state has its own glyph and its own word.
  const icon =
    standing === "locked"
      ? "team-status__icon--locked"
      : standing === "thinking"
        ? "team-status__icon--thinking"
        : standing === "on-deck"
          ? "team-status__icon--on-deck"
          : "team-status__icon--waiting";
  const glyph =
    standing === "locked" ? "✓" : standing === "thinking" ? "◍" : standing === "on-deck" ? "»" : "–";
  const word =
    standing === "locked"
      ? "Locked in"
      : standing === "thinking"
        ? "Thinking"
        : standing === "on-deck"
          ? "Up next"
          : "";

  return (
    <li className={`team-status__player team-status__player--${standing}`}>
      <span className={`team-status__icon ${icon}`} aria-hidden>
        {glyph}
      </span>
      <span className="team-status__name">
        {player.isBot ? <span className="tag tag--bot">BOT</span> : null}
        {player.username}
        {isYou ? <span className="tag tag--quiet">you</span> : null}
      </span>
      {!player.isBot && online ? <span className="dot dot--on" title="Online" aria-label="Online" /> : null}
      {showBallotStats && player.ballots ? (
        <span className="team-status__stat" title={`${player.ballots.chosen} of ${player.ballots.cast} ballots selected`}>
          {player.ballots.cast === 0
            ? "No votes"
            : `${Math.round((player.ballots.chosen / player.ballots.cast) * 100)}% selected`}
        </span>
      ) : previousMove ? (
        <span
          className={`team-status__last-move${previousMove.chosen ? " team-status__last-move--chosen" : ""}`}
          title={previousMove.chosen ? "Selected last turn" : "Played last turn"}
        >
          <span>Last</span> {previousMove.san}
        </span>
      ) : <span className="team-status__word">{word}</span>}
    </li>
  );
}

function TeamBlock({
  side,
  players,
  game,
  userId,
  online,
}: {
  side: Side;
  players: PlayerDto[];
  game: GameDto;
  userId: string;
  online: string[];
}) {
  const isTheirTurn = game.status === "active" && game.turn === side;
  const popcorn = isPopcorn(game.democracyVoteMode);
  const voters = game.democracy?.voters ?? [];
  const locked = players.filter((player) => voters.includes(player.userId)).length;
  const previousResolution = game.democracy?.resolutions.find((resolution) => resolution.side === side);

  const standingFor = (player: PlayerDto): Standing => {
    // Popcorn is a rotation, not a vote: exactly one teammate is thinking and
    // one is warming up, whether or not this side is currently on move.
    if (popcorn) {
      if (isTheirTurn && game.democracy?.activePlayerId === player.userId) return "thinking";
      if (isTheirTurn && game.democracy?.nextPlayerId === player.userId) return "on-deck";
      return "waiting";
    }
    if (!isTheirTurn) return "waiting";
    // Outside democracy there are no ballots; being on turn is the whole story.
    if (!game.democracy) return "thinking";
    return voters.includes(player.userId) ? "locked" : "thinking";
  };

  const previousMoveFor = (player: PlayerDto): ResolvedMove | null => {
    if (isTheirTurn || !previousResolution) return null;
    const proposal = previousResolution.proposals.find((entry) => entry.userId === player.userId);
    return proposal ? { san: proposal.san, chosen: proposal.chosen } : null;
  };

  return (
    <div className="team-status__team">
      <div className="team-status__team-head">
        <span className={`piece-dot piece-dot--${side}`} aria-hidden />
        <span className="team-status__team-name">{side === "white" ? "White" : "Black"}</span>
        <span className="muted team-status__count">
          {popcorn
            ? // A rotation reads better as a position than as a vote tally.
              `${(turnsTakenBy(game.moves.length, side) % Math.max(1, players.length)) + 1} of ${players.length}`
            : !isTheirTurn
              ? "waiting"
              : game.democracy
                ? `${locked} of ${players.length} locked`
                : "to move"}
        </span>
      </div>
      <ul className="team-status__players">
        {players.map((player) => (
          <PlayerRow
            key={player.userId}
            player={player}
            standing={standingFor(player)}
            isYou={player.userId === userId}
            online={online.includes(player.userId)}
            showBallotStats={game.status === "finished" && game.mode === "democracy"}
            previousMove={previousMoveFor(player)}
          />
        ))}
      </ul>
    </div>
  );
}

export function TeamStatus({
  game,
  gameAt,
  userId,
  online,
}: {
  game: GameDto;
  gameAt: number;
  userId: string;
  online: string[];
}) {
  const moveNumber = Math.floor(game.moves.length / 2) + 1;
  const active = game.status === "active";

  return (
    <section className="card team-status" aria-label="Team status">
      <div className="team-status__head">
        <span className="team-status__move">Move {moveNumber}</span>
        {active ? (
          <TurnTimer elapsedMs={game.turnElapsedMs} asOf={gameAt} running />
        ) : (
          <span className="team-status__elapsed">
            {game.status === "waiting" ? "not started" : "finished"}
          </span>
        )}
      </div>

      <TeamBlock
        side="white"
        players={game.players.filter((player) => player.side === "white")}
        game={game}
        userId={userId}
        online={online}
      />
      <TeamBlock
        side="black"
        players={game.players.filter((player) => player.side === "black")}
        game={game}
        userId={userId}
        online={online}
      />
    </section>
  );
}
